import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '../../lib/utils';
import { getThemeClass } from '../../lib/theme';

interface NavigationItem {
  name: string;
  href: string;
  description: string;
}

const navigation: NavigationItem[] = [
  {
    name: 'About',
    href: '/about',
    description: 'Background, working approach, and current focus'
  },
  {
    name: 'Professional Journey',
    href: '/journey',
    description: 'Domain expertise and technical evolution'
  },
  {
    name: 'Research & Publications',
    href: '/research',
    description: 'Published works and technical contributions'
  },
  {
    name: 'Community Impact',
    href: '/community',
    description: 'Leadership and social initiatives'
  },
  {
    name: 'Resume',
    href: '/resume',
    description: 'Resume'
  },
  {
    name: 'Contact',
    href: '/contact',
    description: 'Professional connections and contact information'
  }
];

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolled]);

  // Determine if we should show transparent navigation
  const shouldBeTransparent = isHomePage && !scrolled;

  return (
    <nav className={cn(
      'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
      shouldBeTransparent 
        ? 'bg-transparent' 
        : 'bg-white/90 backdrop-blur-sm shadow-md',
      getThemeClass('colors.border.light')
    )}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo/Home Link */}
          <Link 
            to="/" 
            className={cn(
              "flex items-center",
              "transition-colors duration-300",
              shouldBeTransparent
                ? 'text-white'
                : getThemeClass('colors.text.primary')
            )}
          >
            <span className="text-xl font-semibold">CT</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex md:items-center md:space-x-8">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={cn(
                  "inline-flex items-center px-1 pt-1",
                  "text-sm font-medium",
                  "transition-colors duration-300",
                  "border-b-2",
                  location.pathname === item.href
                    ? [
                        getThemeClass('colors.primary.main'),
                        getThemeClass('colors.primary.main', 'borderHover')
                      ]
                    : [
                        shouldBeTransparent 
                          ? 'text-white/80 hover:text-white' 
                          : [getThemeClass('colors.text.secondary'), getThemeClass('colors.text.hover')],
                        'border-transparent'
                      ]
                )}
                title={item.description}
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* Mobile menu button */}
          <div className="flex items-center md:hidden">
            <button
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              className={cn(
                "inline-flex items-center justify-center p-2 rounded-md",
                "transition-colors duration-300",
                shouldBeTransparent
                  ? 'text-white hover:text-white hover:bg-white/10'
                  : [
                      getThemeClass('colors.text.secondary'),
                      getThemeClass('colors.text.hover'),
                      getThemeClass('colors.background.hover')
                    ]
              )}
              aria-controls="mobile-menu"
              aria-expanded={isOpen}
            >
              <span className="sr-only">
                {isOpen ? 'Close main menu' : 'Open main menu'}
              </span>
              {isOpen ? (
                <X className="block h-6 w-6" aria-hidden="true" />
              ) : (
                <Menu className="block h-6 w-6" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden bg-white shadow-lg"
          >
            <div className="pt-2 pb-3 space-y-1">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    "block pl-3 pr-4 py-2",
                    "text-base font-medium",
                    "transition-colors duration-300",
                    "border-l-4",
                    location.pathname === item.href
                      ? [
                          getThemeClass('colors.primary.main'),
                          getThemeClass('colors.primary.surface'),
                          getThemeClass('colors.primary.main', 'borderHover')
                        ]
                      : [
                          getThemeClass('colors.text.secondary'),
                          getThemeClass('colors.background.hover'),
                          'border-transparent',
                          getThemeClass('colors.text.hover')
                        ]
                  )}
                  onClick={() => setIsOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navigation;
